import { convertPercentageToDecimal, roundTwoDecimals } from '../utils/utils';

export const defaultSliderInitialInputs = {
  iRetAge: 65,
  iIncRep: 80,
  iEECont: 16,
  iROR: 6,
};

export const fixedAssumptionsData = {
  cAge: 56,
  c401k: 364313,
  cHSA: 42665,
  cPS: 152193,
  Inf: convertPercentageToDecimal(3),
  SScal: convertPercentageToDecimal(3),
  cPay: 128250,
};

// Account Projections
// - for age = cAge to iRetAge
const r401k = (
  {
    c401k,
    iROR,
    age,
    cAge,
    iEECont,
    cPay,
    SScal,
  }: {
    c401k: number,
    iROR: number,
    age: number,
    cAge: number,
    iEECont: number,
    cPay: number,
    SScal: number,
  },
): number => roundTwoDecimals(c401k * ((1 + iROR) ** (age - cAge)) + iEECont * cPay * ((((1 + iROR) ** (age - cAge)) - ((1 + SScal) ** (age - cAge))) / (iROR - SScal)));

const rHSA = (
  {
    cHSA,
    iROR,
    age,
    cAge,
    SScal,
  }: {
    cHSA: number,
    iROR: number,
    age: number,
    cAge: number,
    SScal: number,
  },
): number => roundTwoDecimals(cHSA * ((1 + iROR) ** (age - cAge)) + 2000 * ((((1 + iROR) ** (age - cAge)) - ((1 + SScal) ** (age - cAge))) / (iROR - SScal)));

const rPS = (
  {
    cPS,
    iROR,
    age,
    cAge,
    cPay,
    SScal,
  }: {
    cPS: number,
    iROR: number,
    age: number,
    cAge: number,
    cPay: number,
    SScal: number,
  },
): number => roundTwoDecimals(cPS * ((1 + iROR) ** (age - cAge)) + (0.02 * cPay) * ((((1 + iROR) ** (age - cAge)) - ((1 + SScal) ** (age - cAge))) / (iROR - SScal)));

// - for iRetAge + 1 to 100
const pr401k = (
  {
    iROR,
    r401kPrevAgeValue,
    expenseAllocation401kValue,
  }: {
    iROR: number,
    r401kPrevAgeValue: number,
    expenseAllocation401kValue: number,
  },
): number => roundTwoDecimals(Math.max(0, r401kPrevAgeValue * (1 + iROR) - expenseAllocation401kValue));

const prHSA = (
  {
    iROR,
    rHSAPrevAgeValue,
    expenseAllocationHSAValue,
  }: {
    iROR: number,
    rHSAPrevAgeValue: number,
    expenseAllocationHSAValue: number,
  },
): number => roundTwoDecimals(Math.max(0, rHSAPrevAgeValue * (1 + iROR) - expenseAllocationHSAValue));

const prPS = (
  {
    iROR,
    rPSAPrevAgeValue,
    expenseAllocationPSValue,
  }: {
    iROR: number,
    rPSAPrevAgeValue: number,
    expenseAllocationPSValue: number,
  },
): number => roundTwoDecimals(Math.max(0, rPSAPrevAgeValue * (1 + iROR) - expenseAllocationPSValue));

// Annuity Projections
const pSS = ({ age, cAge, Inf }: { age: number, cAge: number, Inf: number },
): number => roundTwoDecimals(24000 * ((1 + Inf) ** (age - cAge)));

const pFroz = ({ iRetAge }: { iRetAge: number }): number => roundTwoDecimals(7484 * (1.05 ** (iRetAge - 65)));

const pCB = ({ iRetAge }: { iRetAge: number }): number => roundTwoDecimals(1700 * (1.05 ** (iRetAge - 65)));

const pFAP = ({ iRetAge }: { iRetAge: number }): number => roundTwoDecimals(24326 * (1.05 ** (iRetAge - 65)));

// Expense Stream
// - Expense Projection - from iRetAge to Age 100
const pExp = (
  {
    age,
    cAge,
    iIncRep,
    cPay,
    Inf,
  }: {
    age: number,
    cAge: number,
    iIncRep: number,
    cPay: number,
    Inf: number,
  },
): number => roundTwoDecimals(iIncRep * cPay * ((1 + Inf) ** (age - cAge)));

// - Expense to be Covered by Accounts - For each age from iRetAge to 100
const totalExp = (
  {
    pExpValue,
    pSSValue,
    pFrozValue,
    pCBValue,
    pFAPValue,
  }: {
    pExpValue: number,
    pSSValue: number,
    pFrozValue: number,
    pCBValue: number,
    pFAPValue: number,
  },
): number => roundTwoDecimals(Math.max(0, pExpValue - (pSSValue + pFrozValue + pCBValue + pFAPValue)));

const expenseAllocationDenominator = (
  {
    r401kValue,
    rHSAValue,
    rPSValue,
    iROR,
    age,
    iRetAge,
  }: {
    r401kValue: number,
    rHSAValue: number,
    rPSValue: number,
    iROR: number,
    age: number,
    iRetAge: number,
  },
): number => roundTwoDecimals((r401kValue * ((1 + iROR) ** (age - 1 - iRetAge))) + (rHSAValue * ((1 + iROR) ** (age - 1 - iRetAge))) + (rPSValue * ((1 + iROR) ** (age - 1 - iRetAge))));

const expenseAllocation401k = (
  {
    totalExpValue,
    r401kValue,
    rHSAValue,
    rPSValue,
    iROR,
    age,
    iRetAge,
  }: {
    totalExpValue: number,
    r401kValue: number,
    rHSAValue: number,
    rPSValue: number,
    iROR: number,
    age: number,
    iRetAge: number,
  },
): number => roundTwoDecimals(totalExpValue * ((r401kValue * ((1 + iROR) ** (age - 1 - iRetAge))) / expenseAllocationDenominator({
  r401kValue,
  rHSAValue,
  rPSValue,
  iROR,
  age,
  iRetAge,
})));

const expenseAllocationHSA = (
  {
    totalExpValue,
    r401kValue,
    rHSAValue,
    rPSValue,
    iROR,
    age,
    iRetAge,
  }: {
    totalExpValue: number,
    r401kValue: number,
    rHSAValue: number,
    rPSValue: number,
    iROR: number,
    age: number,
    iRetAge: number,
  },
): number => roundTwoDecimals(totalExpValue * ((rHSAValue * ((1 + iROR) ** (age - 1 - iRetAge))) / expenseAllocationDenominator({
  r401kValue,
  rHSAValue,
  rPSValue,
  iROR,
  age,
  iRetAge,
})));

const expenseAllocationPS = (
  {
    totalExpValue,
    r401kValue,
    rHSAValue,
    rPSValue,
    iROR,
    age,
    iRetAge,
  }: {
    totalExpValue: number,
    r401kValue: number,
    rHSAValue: number,
    rPSValue: number,
    iROR: number,
    age: number,
    iRetAge: number,
  },
): number => roundTwoDecimals(totalExpValue * ((rPSValue * ((1 + iROR) ** (age - 1 - iRetAge))) / expenseAllocationDenominator({
  r401kValue,
  rHSAValue,
  rPSValue,
  iROR,
  age,
  iRetAge,
})));

export const generateSeries = (
  {
    chartType = 'bar',
    ageRange,
    iROR,
    iEECont,
    iRetAge,
    cAge,
    cHSA,
    c401k,
    cPS,
    cPay,
    SScal,
    iIncRep,
    Inf,
  }: {
    chartType: 'bar' | 'area',
    ageRange: number[],
    iROR: number,
    iEECont: number,
    iRetAge: number,
    cAge: number,
    cHSA: number,
    c401k: number,
    cPS: number,
    cPay: number,
    SScal: number,
    iIncRep: number,
    Inf: number
  }) => {

  const pFrozValue = pFroz({ iRetAge });
  const pCBValue = pCB({ iRetAge });
  const pFAPValue = pFAP({ iRetAge });

  let pSSValues: number[] = [];
  let pFrozValues: number[] = [];
  let pCBValues: number[] = [];
  let pFAPValues: number[] = [];

  const r401kValueAtRetAge = r401k({ c401k, iROR, age: iRetAge, cAge, iEECont, cPay, SScal });
  const rHSAValueAtRetAge = rHSA({ cHSA, iROR, age: iRetAge, cAge, SScal });
  const rPSValueAtRetAge = rPS({ cPS, iROR, age: iRetAge, cAge, cPay, SScal });

  let r401kPrevAgeValue = 0;
  let rHSAPrevAgeValue = 0;
  let rPSAPrevAgeValue = 0;

  const r401kValues = ageRange.map((age) => {
    let pSSValueAux = 0;
    let pFrozValueAux = 0;
    let pCBValueAux = 0;
    let pFAPValueAux = 0;

    let pExpValue = 0;
    let totalExpValue = 0;
    let expenseAllocation401kValue = 0;

    if (age < iRetAge) {

    } else {
      //Filling these values in the first ageRange map to avoid unnecessary iterations
      pSSValueAux = pSS({ age, cAge, Inf });
      pFrozValueAux = pFrozValue;
      pCBValueAux = pCBValue;
      pFAPValueAux = pFAPValue;

      pExpValue = pExp({ age, cAge, cPay, Inf, iIncRep });
      totalExpValue = totalExp({
        pExpValue,
        pSSValue: pSSValueAux,
        pFrozValue,
        pCBValue,
        pFAPValue,
      });

      expenseAllocation401kValue = expenseAllocation401k({
        r401kValue: r401kValueAtRetAge,
        age,
        iRetAge,
        iROR,
        totalExpValue,
        rHSAValue: rHSAValueAtRetAge,
        rPSValue: rPSValueAtRetAge,
      });
    }

    pSSValues.push(pSSValueAux);
    pFrozValues.push(pFrozValueAux);
    pCBValues.push(pCBValueAux);
    pFAPValues.push(pFAPValueAux);

    let pr401kValue;

    if (age <= iRetAge) {
      pr401kValue = r401k({ c401k, iROR, age, cAge, iEECont, cPay, SScal });
    } else {
      pr401kValue = pr401k({ iROR, r401kPrevAgeValue, expenseAllocation401kValue });
    }

    r401kPrevAgeValue = pr401kValue;

    return Math.min(expenseAllocation401kValue, pr401kValue);
  });

  const rHSAValues = ageRange.map((age) => {
    let pSSValueAux = 0;

    let pExpValue = 0;
    let totalExpValue = 0;
    let expenseAllocationHSAValue = 0;

    if (age < iRetAge) {

    } else {
      pSSValueAux = pSS({ age, cAge, Inf });

      pExpValue = pExp({ age, cAge, cPay, Inf, iIncRep });
      totalExpValue = totalExp({
        pExpValue,
        pSSValue: pSSValueAux,
        pFrozValue,
        pCBValue,
        pFAPValue,
      });

      expenseAllocationHSAValue = expenseAllocationHSA({
        r401kValue: r401kValueAtRetAge,
        age,
        iRetAge,
        iROR,
        totalExpValue,
        rHSAValue: rHSAValueAtRetAge,
        rPSValue: rPSValueAtRetAge,
      });
    }

    let prHSAValue;

    if (age <= iRetAge) {
      prHSAValue = rHSA({ cHSA, iROR, age, cAge, SScal });
    } else {
      prHSAValue = prHSA({ iROR, rHSAPrevAgeValue, expenseAllocationHSAValue });
    }

    rHSAPrevAgeValue = prHSAValue;

    return Math.min(expenseAllocationHSAValue, prHSAValue);
  });

  const rPSValues = ageRange.map((age) => {
    let pSSValueAux = 0;

    let pExpValue = 0;
    let totalExpValue = 0;
    let expenseAllocationPSValue = 0;

    if (age < iRetAge) {
    } else {
      pSSValueAux = pSS({ age, cAge, Inf });

      pExpValue = pExp({ age, cAge, cPay, Inf, iIncRep });
      totalExpValue = totalExp({
        pExpValue,
        pSSValue: pSSValueAux,
        pFrozValue,
        pCBValue,
        pFAPValue,
      });

      expenseAllocationPSValue = expenseAllocationPS({
        r401kValue: r401kValueAtRetAge,
        age,
        iRetAge,
        iROR,
        totalExpValue,
        rHSAValue: rHSAValueAtRetAge,
        rPSValue: rPSValueAtRetAge,
      });
    }

    let prPSValue;

    if (age <= iRetAge) {
      prPSValue = rPS({ cPS, iROR, age, cAge, cPay, SScal });
    } else {
      prPSValue = prPS({ iROR, rPSAPrevAgeValue, expenseAllocationPSValue });
    }

    rPSAPrevAgeValue = prPSValue;

    return Math.min(expenseAllocationPSValue, prPSValue);
  });

  return [
    {
      name: '401(k) Plan',
      type: 'area',
      data: r401kValues,
    },
    {
      name: '401(k) Plan',
      type: 'bar',
      data: r401kValues,
    }, {
      name: 'HSA',
      type: 'area',
      data: rHSAValues,
    }, {
      name: 'HSA',
      type: 'bar',
      data: rHSAValues,
    }, {
      name: 'Personal Savings',
      type: 'area',
      data: rPSValues,
    }, {
      name: 'Personal Savings',
      type: 'bar',
      data: rPSValues,
    }, {
      name: 'Social Security',
      type: 'bar',
      data: pSSValues,
    }, {
      name: 'Frozen Plan',
      type: 'bar',
      data: pFrozValues,
    }, {
      name: 'Cash Balance Plan',
      type: 'bar',
      data: pCBValues,
    }, {
      name: 'Final Average Pay Plan',
      type: 'bar',
      data: pFAPValues,
    },
  ].filter((item) => item.type === chartType);
};